import type { Expense } from '#/graphql/types';
import type { ColumnProps } from '#/common/components/DataGrid';
import { statusItems, typeItems } from '../../utils/constants';
import { withChip } from '#/common/utils/wrappers';
import { color } from '@nuvolo/nuux-theme/utils';
import { CustomChipColors } from '#/common/utils/wrappers/withChip';
import { gql } from '@apollo/client';
import { getTenantList, getUserList, getWorkOrderList } from '#/graphql/queries';

// Custom color schemes for expense status chips
export const expenseStatusColors: Record<string, CustomChipColors> = {
  draft: {
    backgroundColor: color('info', 'dark', 0.15),
    textColor: color('info', 'dark'),
  },
  expended: {
    backgroundColor: color('success', 'dark', 0.15),
    textColor: color('success', 'dark'),
  },
};

const columns: ColumnProps<Expense>[] = [
  { dataField: 'id', caption: 'ID', visible: false, showInColumnChooser: false },
  {
    dataField: 'displayNumber',
    caption: 'Number',
    visible: true,
    openOnClick: true,
    fixed: true,
    dataType: 'string',
    sortIndex: 0,
    sortOrder: 'desc',
  },
  {
    dataField: 'type',
    caption: 'Type',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: typeItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
  },
  {
    dataField: 'title',
    caption: 'Title',
    visible: true,
    openOnClick: true,
    dataType: 'string',
  },
  {
    dataField: 'description',
    caption: 'Description',
    visible: true,
  },
  {
    dataField: 'workOrder.title',
    caption: 'Work Order',
    visible: true,
    openOnClick: true,
    onClick: (e) => {
      e.navigate(`/work-orders/${e.row.workOrderId ?? ''}`);
    },
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getWorkOrderList),
      displayExpr: 'title',
    },
  },
  {
    dataField: 'status',
    caption: 'Status',
    visible: true,
    dataType: 'string',
    lookup: {
      nullable: true,
      dataSource: statusItems,
      displayExpr: 'label',
      valueExpr: 'key',
    },
    cellRender: (e) => {
      if (!e.displayValue || e.displayValue === '') {
        return null;
      }
      const customColors = expenseStatusColors[e.value as string];

      return withChip(e.displayValue, 'normal', customColors);
    },
  },
  {
    dataField: 'amount',
    caption: 'Amount',
    visible: true,
    dataType: 'number',
    format: {
      type: 'currency',
      precision: 2,
    },
  },
  {
    dataField: 'user.name',
    caption: 'Created By',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },

  // Hidden Columns
  // {
  //   dataField: 'purchaseOrderId',
  //   caption: 'Purchase Order',
  //   visible: false,
  // },
  {
    dataField: 'tenant.name',
    caption: 'Organization',
    visible: true,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getTenantList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'createdAt',
    caption: 'Created At',
    visible: false,
    dataType: 'date',
  },
  {
    dataField: 'updatedByUser.name',
    caption: 'Updated By',
    visible: false,
    dataType: 'string',
    headerFilter: {
      getItemList: gql(getUserList),
      displayExpr: 'name',
    },
  },
  {
    dataField: 'updatedAt',
    caption: 'Updated At',
    visible: false,
    dataType: 'date',
  },
  {
    dataField: 'workOrderId',
    caption: 'Work Order ID',
    visible: false,
    dataType: 'string',
    showInColumnChooser: false,
  },
];

export default columns;
